/**
 * @file Фильтр по времени работы клуба.
 * Парсит JSON-расписание из data-атрибута элемента и проверяет,
 * открыт ли клуб в заданный момент времени или работает круглосуточно.
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const ALWAYS_OPEN_PATTERN = /24\s*(?:\/\s*7|hours?|hrs?|h)|round\s+the\s+clock/i
const DATE_TIME_FORMATTERS = new Map()

/**
 * Парсит JSON-значение из data-атрибута в объект расписания `{ Mon: "09:00-22:00", ... }`.
 * Поддерживает два формата:
 * - массив `[{"day":"Mon","time":"09:00-22:00"}, ...]`
 * - объект `{"Mon":"09:00-22:00", ...}`
 * @param {string|null} rawValue - Сериализованное JSON-расписание.
 * @returns {Object.<string, string>} Объект вида `{ Mon: "09:00-22:00" }` или `{}` при ошибке.
 */
function parseWorkingHours(rawValue) {
  if (!rawValue) return {}

  try {
    const parsedValue = JSON.parse(rawValue)

    if (Array.isArray(parsedValue)) {
      return parsedValue.reduce((schedule, entry) => {
        if (entry?.day) schedule[entry.day] = entry.time

        return schedule
      }, {})
    }

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {}
  } catch {
    return {}
  }
}

/**
 * Проверяет, соответствует ли расписание работы клуба критериям фильтра.
 * Поддерживаемые значения фильтра: `"now"` (открыт сейчас) и `"24_hours"` (работает 24/7).
 * @param {{ schedule: Object.<string, string>, timeZone: string|null }} itemValue
 * @param {Set<string>} filterValues - Активные значения фильтра.
 * @param {{ now?: Date }} [context={}]
 * @returns {boolean}
 */
function matchesWorkingHours(itemValue, filterValues, context = {}) {
  const { schedule, timeZone } = itemValue
  if (!schedule || !(filterValues instanceof Set)) return false

  return [...filterValues].some((filterValue) => {
    if (filterValue === '24_hours') return isOpen24Hours(schedule)
    if (filterValue === 'now') {
      return isOpenAt(schedule, context.now, timeZone)
    }

    return false
  })
}

/**
 * Проверяет, работает ли клуб круглосуточно (все дни покрывают интервал 24 часа или есть пометка "24/7").
 * @param {Object.<string, string|string[]>} schedule
 * @returns {boolean}
 */
function isOpen24Hours(schedule) {
  if (isAlwaysOpenValue(schedule.Spec)) return true

  return DAYS.every((day) => {
    return scheduleValues(schedule[day]).some(isAlwaysOpenValue)
  })
}

/**
 * Проверяет, открыт ли клуб в заданный момент времени.
 * Учитывает временную зону клуба и переход суток (например, интервал `22:00-02:00`).
 * @param {Object.<string, string|string[]>} schedule
 * @param {Date} suppliedDate
 * @param {string|null} timeZone - IANA-идентификатор часового пояса.
 * @returns {boolean}
 */
function isOpenAt(schedule, suppliedDate, timeZone) {
  const date = suppliedDate instanceof Date ? suppliedDate : new Date()
  const { day: currentDay, minutes: currentMinutes } = dateParts(date, timeZone)
  const currentDayIndex = DAYS.indexOf(currentDay)
  const previousDay = DAYS[(currentDayIndex + DAYS.length - 1) % DAYS.length]

  if (scheduleValues(schedule.Spec).some(isAlwaysOpenValue)) return true

  const openInCurrentDay = scheduleValues(schedule[currentDay]).some((value) =>
    isOpenDuringCurrentDay(value, currentMinutes)
  )

  if (openInCurrentDay) return true

  return scheduleValues(schedule[previousDay]).some((value) => {
    return isOpenFromPreviousDay(value, currentMinutes)
  })
}

function dateParts(date, timeZone) {
  if (!timeZone) return localDateParts(date)

  try {
    const parts = formatterFor(timeZone).formatToParts(date)
    const day = parts.find((part) => part.type === 'weekday')?.value
    const hours = Number(parts.find((part) => part.type === 'hour')?.value)
    const minutes = Number(parts.find((part) => part.type === 'minute')?.value)

    if (
      DAYS.includes(day) &&
      Number.isInteger(hours) &&
      Number.isInteger(minutes)
    ) {
      return { day, minutes: hours * 60 + minutes }
    }
  } catch {
    return localDateParts(date)
  }

  return localDateParts(date)
}

function formatterFor(timeZone) {
  if (!DATE_TIME_FORMATTERS.has(timeZone)) {
    DATE_TIME_FORMATTERS.set(
      timeZone,
      new Intl.DateTimeFormat('en-US-u-hc-h23', {
        timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      })
    )
  }

  return DATE_TIME_FORMATTERS.get(timeZone)
}

function localDateParts(date) {
  return {
    day: DAYS[date.getDay()],
    minutes: date.getHours() * 60 + date.getMinutes()
  }
}

function scheduleValues(value) {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value.trim() !== '') return [value]

  return []
}

function isAlwaysOpenValue(value) {
  if (typeof value !== 'string') return false
  if (ALWAYS_OPEN_PATTERN.test(value)) return true

  return parseIntervals(value).some(({ from, to }) => from === to)
}

function isOpenDuringCurrentDay(value, currentMinutes) {
  if (isAlwaysOpenValue(value)) return true

  return parseIntervals(value).some(({ from, to }) => {
    if (from < to) return currentMinutes >= from && currentMinutes < to

    return currentMinutes >= from
  })
}

function isOpenFromPreviousDay(value, currentMinutes) {
  if (isAlwaysOpenValue(value)) return false

  return parseIntervals(value).some(({ from, to }) => {
    return from > to && currentMinutes < to
  })
}

function parseIntervals(value) {
  if (typeof value !== 'string') return []

  const intervals = []
  const pattern =
    /(\d{1,2}):(\d{2})\s*(AM|PM)?\s*[-–—]\s*(\d{1,2}):(\d{2})\s*(AM|PM)?/gi

  for (const match of value.matchAll(pattern)) {
    const from = timePartsToMinutes(match[1], match[2], match[3])
    const to = timePartsToMinutes(match[4], match[5], match[6])

    if (from !== null && to !== null) intervals.push({ from, to })
  }

  return intervals
}

function timePartsToMinutes(rawHours, rawMinutes, rawPeriod) {
  let hours = Number(rawHours)
  const minutes = Number(rawMinutes)
  const period = rawPeriod?.toUpperCase()

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null

  if (period && hours <= 12) {
    if (period === 'AM' && hours === 12) hours = 0
    if (period === 'PM' && hours < 12) hours += 12
  }

  return hours * 60 + minutes
}

export { matchesWorkingHours, parseWorkingHours }
